/**
 * Members — everyone a Wedding Life Book has touched.
 *
 * THREE POPULATIONS, ONE LIST: the couples who bought a book, the coordinators
 * they delegated to, and the guests who went on to become LifeScribe members.
 * The server unions them with one projection so a single grid can page and sort
 * across all three in SQL.
 *
 * ⚠️ COORDINATORS ARE NOT IN PRODUCTION. `wedding_coordinators` is migration
 * 459 and exists only in the sandbox, so against the production database the
 * server answers `available: false` with a reason and every coordinator count
 * NULL. This page prints that sentence instead of a zero — "0 coordinators" and
 * "we cannot see coordinators from here" are different facts, and only one of
 * them is true.
 *
 * ⚠️ THE LIST IS ALL-TIME BY DEFAULT, THE KPIs ARE NOT. "Who has ever joined"
 * is the question the newest-members table answers, and a three-day range would
 * empty it. The KPIs above it are windowed, and each says which it is.
 *
 * ⚠️ AND THE FRICTION TILES ARE NEITHER. A lockout is a state that exists RIGHT
 * NOW and expires on its own; nothing records the event, so those three numbers
 * cannot be windowed at all. They sit under a date filter that does not apply to
 * them, which is why each one says so in its own subtitle — a number that
 * silently ignores the pill above it is the same class of lie as a structural
 * zero.
 *
 * The list filters (`?role=`, `?q=`) live in the URL alongside the range, so a
 * filtered view is a link. Page and sort stay local.
 */

import React from 'react';
import {
  Box, Card, CardContent, Chip, Grid, LinearProgress, MenuItem, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/FileDownload';
import InputAdornment from '@mui/material/InputAdornment';
import {
  DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams, GridSortModel,
} from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import EmptyState from '../../common/EmptyState';
import TimeRangeFilter from '../../common/TimeRangeFilter';
import useTimeRangeParam from '../../common/useTimeRangeParam';
import useUrlParam from '../../common/useUrlParam';
import { describeRange, resolveRangeYmd, toApiToken } from '../../common/timeRange';
import { csvUrl, startCsvDownload } from '../csvDownload';
import WeddingKpiCard from '../WeddingKpiCard';
import RangeMetaCaption from '../RangeMetaCaption';
import AcquisitionMixCard, { sourceLabel } from './AcquisitionMixCard';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type {
  MemberRole, MemberRow, MembersAuthFriction, MembersListResponse, MembersResponse,
} from '../weddingApi';
import { num, relativeTime, shortDate } from '../weddingFormat';

/** Mirrors MEMBER_SORT_COLUMNS in weddingAdmin.js. Anything else is ignored. */
const SORTABLE = new Set(['joined_at', 'name', 'email', 'role', 'books', 'status']);

/** Everything `?role=` may hold — MEMBER_ROLES on the server. */
const ROLE_PARAM_VALUES: readonly string[] = ['couple', 'coordinator', 'member'];

const ROLE_LABEL: Record<MemberRole, string> = {
  couple: 'Couple',
  coordinator: 'Coordinator',
  member: 'Guest → member',
};

const ROLE_COLOR: Record<MemberRole, 'primary' | 'secondary' | 'error'> = {
  couple: 'primary',
  coordinator: 'secondary',
  member: 'error',
};

/**
 * Q4 — the server ships its own definitions; render THOSE, never a restatement.
 * `definition` is an open record, so a key that has not shipped yet reads as
 * undefined and the tooltip falls back rather than printing "[object Object]".
 */
function definitionOf(friction: MembersAuthFriction | undefined, key: string): string | undefined {
  const value = friction?.definition?.[key];
  return typeof value === 'string' ? value : undefined;
}

/**
 * "new" is a claim, and an empty period has nothing to claim.
 *
 * `delta_pct: null` means the prior period was zero. That reads as "new" when
 * something happened this period, and as noise when nothing did — so zero
 * against zero gets no chip rather than a badge announcing nothing's arrival.
 */
function deltaFor(
  block: { value: import('../weddingApi').Numeric | null; delta_pct: number | null } | undefined
): { deltaPct?: number | null; showDelta: boolean } {
  if (!block) return { showDelta: false };
  const measured = block.value !== null && block.value !== undefined && num(block.value) > 0;
  if (block.delta_pct === null && !measured) return { showDelta: false };
  return { deltaPct: block.delta_pct, showDelta: true };
}

const WeddingMembersPage: React.FC = () => {
  const navigate = useNavigate();
  const [range, setRange] = useTimeRangeParam({ type: '30d' });

  const [summary, setSummary] = React.useState<MembersResponse | null>(null);
  const [list, setList] = React.useState<MembersListResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = React.useState(true);
  const [listLoading, setListLoading] = React.useState(true);
  const [summaryError, setSummaryError] = React.useState<string | null>(null);
  const [listError, setListError] = React.useState<string | null>(null);

  const [roleParam, setRoleParam] = useUrlParam('role', { allowed: ROLE_PARAM_VALUES });
  const role = roleParam as MemberRole | '';
  const [search, setSearch] = useUrlParam('q');

  // Seeded from the URL so a pasted `?q=okafor` shows the word it filtered by.
  const [searchInput, setSearchInput] = React.useState(search);

  const [pagination, setPagination] = React.useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'joined_at', sort: 'desc' },
  ]);

  // Refs so the debounce depends on the TYPED value alone — the setter's
  // identity changes with every URL write. See WeddingBooksPage.
  const searchRef = React.useRef(search);
  searchRef.current = search;
  const setSearchRef = React.useRef(setSearch);
  setSearchRef.current = setSearch;

  // 350 ms, matching every other wedding grid.
  React.useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed === searchRef.current) return;
      setSearchRef.current(trimmed);
      setPagination((p) => ({ ...p, page: 0 }));
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  /**
   * ALWAYS the token AND the resolved local calendar dates, presets included. A
   * bare token would let the server resolve its own rolling millisecond window,
   * so the same pill meant a different span here than on Revenue. The dates are
   * the window; the token only says which pill produced it.
   */
  const rangeParams = React.useCallback(() => {
    const ymd = resolveRangeYmd(range);
    return { range: toApiToken(range), from: ymd.from, to: ymd.to };
  }, [range]);

  const loadSummary = React.useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      setSummary(await weddingApi.getMembers(rangeParams()));
    } catch (e) {
      setSummaryError((e as WeddingApiError).message || 'Could not load the member summary.');
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [rangeParams]);

  const loadList = React.useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const sort = sortModel[0];
      setList(
        await weddingApi.getMembersList({
          page: pagination.page + 1,
          limit: pagination.pageSize,
          ...(role ? { role } : {}),
          ...(search ? { search } : {}),
          ...(sort && SORTABLE.has(sort.field)
            ? { sortBy: sort.field, sortOrder: sort.sort === 'asc' ? 'ASC' : 'DESC' }
            : {}),
        })
      );
    } catch (e) {
      setListError((e as WeddingApiError).message || 'Could not load the members list.');
      setList(null);
    } finally {
      setListLoading(false);
    }
  }, [pagination.page, pagination.pageSize, role, search, sortModel]);

  React.useEffect(() => { loadSummary(); }, [loadSummary]);
  React.useEffect(() => { loadList(); }, [loadList]);

  const rangeLabel = describeRange(range);
  const kpis = summary?.kpis;
  const coordinators = kpis?.coordinators;
  const engagement = summary?.engagement;
  const firstTouch = summary?.first_touch;
  const friction = summary?.auth_friction;

  /*
    THE CSV IS THE LIST. Same role, same search, no page — and no range, because
    the list itself is unwindowed. The API's param is `search` even though the
    URL key is the shorter `q`.
  */
  const exportHref = csvUrl('/members/export.csv', {
    ...(role ? { role } : {}),
    ...(search ? { search } : {}),
  });

  const columns: GridColDef<MemberRow>[] = [
    {
      field: 'name',
      headerName: 'Member',
      flex: 1.4,
      minWidth: 220,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<MemberRow>) => (
        <Box sx={{ overflow: 'hidden' }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {p.row.name || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {p.row.email || 'no email on record'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 160,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<MemberRow>) => (
        <Chip
          size="small"
          variant="outlined"
          color={ROLE_COLOR[p.row.role] || 'primary'}
          label={ROLE_LABEL[p.row.role] || p.row.role}
        />
      ),
    },
    {
      field: 'came_from',
      headerName: 'Came from',
      flex: 1,
      minWidth: 180,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<MemberRow>) => {
        if (!p.row.came_from) {
          return (
            <Tooltip title="Nothing recorded the first touch for this person.">
              <Typography variant="body2" color="text.secondary">—</Typography>
            </Tooltip>
          );
        }
        return (
          <Typography variant="body2" noWrap>
            {p.row.came_from_kind === 'acquisition_source'
              ? sourceLabel(p.row.came_from)
              : p.row.came_from}
          </Typography>
        );
      },
    },
    {
      field: 'books',
      headerName: 'Books',
      width: 90,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (p: GridRenderCellParams<MemberRow>) => (
        <Typography variant="body2">{num(p.row.books).toLocaleString()}</Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<MemberRow>) => (
        <Chip size="small" variant="outlined" label={p.row.status || 'unknown'} />
      ),
    },
    {
      field: 'joined_at',
      headerName: 'Joined',
      width: 150,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<MemberRow>) => (
        <Tooltip title={p.row.joined_at ? shortDate(p.row.joined_at) : ''}>
          <Typography variant="body2" color="text.secondary">
            {p.row.joined_at ? relativeTime(p.row.joined_at) : '—'}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  const engagementRows: { label: string; value: React.ReactNode; hint?: string }[] = [
    {
      label: `Wrote a chapter in ${rangeLabel}`,
      value: engagement ? num(engagement.wrote_chapter).toLocaleString() : '—',
    },
    {
      label: 'Added photographs',
      value: engagement ? num(engagement.added_photos).toLocaleString() : '—',
    },
    {
      label: 'Made a voice recording',
      value: engagement ? num(engagement.made_voice_recording).toLocaleString() : '—',
    },
    {
      label: 'Started their own book after guesting',
      value: engagement?.started_own_book?.available
        ? num(engagement.started_own_book.value).toLocaleString()
        : '—',
      hint: engagement?.started_own_book?.available
        ? 'All time, not the selected range — someone can start their own book months after contributing.'
        : engagement?.started_own_book?.reason
          || 'The LifeScribe chapters table is not readable from here, so this cannot be counted.',
    },
  ];

  const maxUtmTouched = Math.max(
    1,
    ...(summary?.conversion_utm || []).map((u) => num(u.touched))
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <PeopleIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Members
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <TimeRangeFilter
          value={range}
          onChange={setRange}
          onRefresh={() => { loadSummary(); loadList(); }}
          refreshing={summaryLoading || listLoading}
        />
      </Box>
      {/*
        The window, described by the SERVER — the same caption Overview carries.
        RangeMetaCaption's own header says all three windowed screens ship this
        block; for a while only one of them rendered it, so the timezone note
        the server computes reached a third of the pages it was written for.
      */}
      <RangeMetaCaption meta={summary?.range} sx={{ mb: 1 }} />
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Everyone with an account touched by a wedding book — couples, coordinators, and guests who
        joined. The tiles below are measured over {rangeLabel}.
      </Typography>

      {summaryError && (
        <EmptyState
          icon="error"
          title="Could not load the member summary"
          description={summaryError}
          action={{ label: 'Retry', onClick: loadSummary, variant: 'contained' }}
        />
      )}

      {!summaryError && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <WeddingKpiCard
              title="Members touched"
              value={kpis ? num(kpis.members_touched.value).toLocaleString() : '—'}
              subtitle={
                kpis?.members_touched.includes_coordinators
                  ? 'couples, accepted coordinators and converted guests'
                  : 'couples and converted guests (coordinators are not visible here)'
              }
              {...deltaFor(kpis?.members_touched)}
              tone="primary"
              loading={summaryLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <WeddingKpiCard
              title="Couples"
              value={kpis ? num(kpis.couples.total).toLocaleString() : '—'}
              subtitle={
                kpis
                  ? `${num(kpis.couples.value).toLocaleString()} signed up in ${rangeLabel}`
                  : undefined
              }
              {...deltaFor(kpis?.couples)}
              tone="secondary"
              loading={summaryLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            {/*
              THE HONEST ABSENCE. Coordinators live in a table this database
              does not have; a zero here would read as "nobody delegated", which
              is a different and false claim.
            */}
            <WeddingKpiCard
              title="Coordinators"
              value={coordinators?.available ? num(coordinators.accepted).toLocaleString() : '—'}
              subtitle={
                coordinators?.available
                  ? `${num(coordinators.invited_total).toLocaleString()} invited · ${
                    coordinators.accept_rate === null || coordinators.accept_rate === undefined
                      ? '—'
                      : `${Math.round(coordinators.accept_rate * 100)}% accepted`}`
                  : 'Not yet in production — coordinators cannot be counted from this database.'
              }
              {...(coordinators?.available
                ? deltaFor({ value: coordinators.accepted_in_range ?? null, delta_pct: coordinators.delta_pct })
                : { showDelta: false })}
              tone="success"
              loading={summaryLoading}
              tooltip={coordinators?.available ? undefined : coordinators?.reason}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <WeddingKpiCard
              title="Guests who joined"
              value={kpis ? num(kpis.guests_joined.total).toLocaleString() : '—'}
              subtitle={
                kpis
                  ? `${num(kpis.guests_joined.value).toLocaleString()} joined in ${rangeLabel}`
                  : undefined
              }
              {...deltaFor(kpis?.guests_joined)}
              tone="error"
              loading={summaryLoading}
            />
          </Grid>
        </Grid>
      )}

      {/*
        Q4 — SIGN-IN FRICTION, AND IT IS NOT WINDOWED.

        A lockout is a state that exists at this instant and expires on its own;
        no table records the event, so there is no range to scope these to. They
        sit below a date filter that does not apply to them, and every one of
        them says so rather than letting the pill above imply a window.

        Only the couples tile is clickable, because only couples have a list to
        open. A guest lockout is per sign-in session and there is no cross-book
        guest view to send anyone to — a tile that looked clickable and led
        nowhere would be worse than a plain number.
      */}
      {!summaryError && friction && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Sign-in friction
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Point-in-time counts. Lockouts expire on their own and nothing records the event, so
            none of these three can be scoped to the date filter above.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <WeddingKpiCard
                title="Couples locked out now"
                value={num(friction.couples_locked_now).toLocaleString()}
                subtitle="locked out right now — not the date filter"
                valueColor={num(friction.couples_locked_now) > 0 ? 'error.main' : undefined}
                showDelta={false}
                loading={summaryLoading}
                onClick={() => navigate('/dashboard/wedding/couples?locked=locked')}
                tooltip={`${definitionOf(friction, 'couples_locked_now')
                  || 'Couples whose account is locked at this moment.'} Opens the Couples list `
                  + 'filtered to locked accounts.'}
                data-testid="friction-couples-locked"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <WeddingKpiCard
                title="Guests locked out now"
                value={num(friction.guests_locked_now).toLocaleString()}
                subtitle="distinct guests, right now — not the date filter"
                showDelta={false}
                loading={summaryLoading}
                tooltip={`${definitionOf(friction, 'guests_locked_now')
                  || 'Distinct guests with a live lockout — one person, not one row per device.'} `
                  + 'A guest lockout is per sign-in session and clears on its own, so this is not a '
                  + 'queue to work through. There is no cross-book guest list to open.'}
                data-testid="friction-guests-locked"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <WeddingKpiCard
                title="Quiet couples"
                value={num(friction.quiet_couples).toLocaleString()}
                subtitle={`no sign-in in ${num(friction.quiet_after_days)} days, as of right now`}
                showDelta={false}
                loading={summaryLoading}
                tooltip={`${definitionOf(friction, 'quiet_couples')
                  || 'A couple with an active or collecting book who has not signed in lately.'} `
                  + `${num(friction.quiet_couples_never_logged_in).toLocaleString()} of them have `
                  + 'never signed in at all — a subset of this number, not a figure to add beside it.'}
                data-testid="friction-quiet-couples"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={{ borderRadius: '16px', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Newest members
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Every person, all time — paged and sorted by the server.
                {list?.acquisition_source_available === false
                  && ' Couples show “—” for their source until they are asked how they heard about us.'}
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  label="Search"
                  placeholder="Name, email or wedding code"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  sx={{ minWidth: 240 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <TextField
                  select
                  size="small"
                  label="Role"
                  value={role}
                  onChange={(e) => {
                    setRoleParam(e.target.value);
                    setPagination((p) => ({ ...p, page: 0 }));
                  }}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All roles</MenuItem>
                  {(list?.roles || ['couple', 'coordinator', 'member']).map((r) => (
                    <MenuItem key={r} value={r}>{ROLE_LABEL[r] || r}</MenuItem>
                  ))}
                </TextField>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={() => startCsvDownload(exportHref)}
                  disabled={listLoading}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Export CSV
                </Button>
              </Stack>

              {listError ? (
                <EmptyState
                  icon="error"
                  title="Could not load the members list"
                  description={listError}
                  action={{ label: 'Retry', onClick: loadList, variant: 'contained' }}
                />
              ) : (
                <DataGrid
                  rows={list?.members || []}
                  columns={columns}
                  getRowId={(r) => `${r.role}:${r.id}`}
                  loading={listLoading}
                  rowCount={list?.pagination?.total || 0}
                  paginationMode="server"
                  sortingMode="server"
                  filterMode="server"
                  paginationModel={pagination}
                  onPaginationModelChange={setPagination}
                  sortModel={sortModel}
                  onSortModelChange={setSortModel}
                  pageSizeOptions={[25, 50, 100]}
                  disableRowSelectionOnClick
                  autoHeight
                />
              )}

              {list?.coordinators_available === false && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
                  {list.coordinators_reason
                    || 'Coordinators are not in this database, so no coordinator appears in this list.'}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={3}>
            {/*
              E5 — the mix is a CARD OF ITS OWN now, because `(not asked)` had to
              stop being a bar in the same list as Instagram. See
              AcquisitionMixCard for why an absence is not a channel.
            */}
            <AcquisitionMixCard firstTouch={firstTouch} rangeLabel={rangeLabel} />

            <Card sx={{ borderRadius: '16px' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Cross-sell attribution
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Guests who saw the LifeScribe cross-sell, and how many took it.
                </Typography>
                {(summary?.conversion_utm || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No guest has reached the cross-sell yet.
                  </Typography>
                ) : (
                  <Stack spacing={1.25}>
                    {(summary?.conversion_utm || []).slice(0, 6).map((u) => (
                      <Box key={`${u.source}:${u.campaign}`}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                          <Typography variant="body2" noWrap>{sourceLabel(u.source)}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {num(u.converted).toLocaleString()} of {num(u.touched).toLocaleString()}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, (num(u.touched) / maxUtmTouched) * 100)}
                          color="primary"
                          sx={{ height: 7, borderRadius: 4 }}
                          aria-label={`${u.source}: ${num(u.touched)} saw it`}
                        />
                      </Box>
                    ))}
                  </Stack>
                )}
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
                  Cross-sell attribution is all time, not the selected range. Each row counts people within its own source/campaign pair, so somebody whose conversion rows carry two different sources appears in both — these rows attribute channels, they do not add up to the funnel.
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: '16px' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Engagement
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Distinct guests who did each thing.
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Action</TableCell>
                      <TableCell align="right">Guests</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {engagementRows.map((row) => (
                      <TableRow key={row.label}>
                        <TableCell>
                          {row.hint ? (
                            <Tooltip title={row.hint}>
                              <Typography variant="body2">{row.label}</Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2">{row.label}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.value}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {engagement?.note && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
                    {engagement.note}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WeddingMembersPage;
